import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FilterConfig, FilterSelectOption } from '../../utilities/types';

export type FiltersValues = Record<string, any>;

export type FiltersPanelProps = {
  /**
   * Array of filter definitions. Each filter must have a unique 'name'.
   */
  filters: FilterConfig[];

  /**
   * Callback fired when user clicks Search.
   * Receives current filter values (only keys defined in filters array).
   */
  onSearch: (values: FiltersValues) => void;

  /**
   * Optional callback fired when Reset clicked (after internal reset).
   */
  onReset?: () => void;

  /**
   * Optional initial values to pre-populate the filter fields.
   */
  initialValues?: Partial<FiltersValues>;

  /**
   * Optional labels
   */
  searchLabel?: string;
  resetLabel?: string;
};

const ListFiltersWidget: React.FC<FiltersPanelProps> = ({
  filters,
  onSearch,
  onReset,
  initialValues = {},
  searchLabel = 'Search',
  resetLabel = 'Reset',
}) => {
  // Build initial state from config + initialValues
  /* eslint-disable no-restricted-syntax */
  const initialState = useMemo(() => {
    const s: FiltersValues = {};
    for (const f of filters) {
      if (initialValues && Object.prototype.hasOwnProperty.call(initialValues, f.name)) {
        s[f.name] = (initialValues as any)[f.name];
      } else {
        s[f.name] = f.type === 'select' ? '' : '';
      }
    }
    return s;
  }, [filters, initialValues]);

  const [values, setValues] = useState<FiltersValues>(initialState);
  const [expanded, setExpanded] = useState<boolean>(true);

  const handleChange = (name: string, v: any) => {
    setValues((prev) => ({ ...prev, [name]: v }));
  };

  const handleSearch = () => {
    // Send all keys (even empty ones). Caller can ignore empty fields.
    onSearch(values);
  };

  const handleReset = () => {
    setValues(initialState);
    onReset?.();
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded((e) => !e)}
      sx={{
        '& .MuiAccordionSummary-root': {
          minHeight: 'auto !important',
        },
        '& .MuiAccordionSummary-content': {
          margin: '0 !important',
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          borderBottom: '1px solid #ccc',
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          sx={{
            my: '10px',
            '& h6': { my: 0 },
          }}
        >
          Filters
        </Typography>
      </AccordionSummary>

      <AccordionDetails>
        <Box sx={{ width: '100%' }}>
          <Grid container spacing={2}>
            {filters.map((f) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={f.name}>
                {f.type === 'text' ? (
                  <TextField
                    fullWidth
                    label={f.label}
                    margin="normal"
                    placeholder={f.placeholder}
                    value={values[f.name] ?? ''}
                    onChange={(e) => handleChange(f.name, e.target.value)}
                    size="small"
                  />
                ) : (
                  <TextField
                    select
                    fullWidth
                    label={f.label}
                    placeholder={f.placeholder}
                    value={values[f.name] ?? ''}
                    onChange={(e) => handleChange(f.name, e.target.value)}
                    size="small"
                  >
                    {/* optional empty item for "any" */}
                    <MenuItem value="">
                      <em>Select</em>
                    </MenuItem>
                    {(f as any).options.map((opt: FilterSelectOption) => (
                      <MenuItem key={String(opt.value)} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Grid>
            ))}
          </Grid>

          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button variant="contained" onClick={handleSearch}>
              {searchLabel}
            </Button>
            <Button variant="outlined" onClick={handleReset}>
              {resetLabel}
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button size="small" onClick={() => setExpanded(false)} sx={{ alignSelf: 'center' }}>
              Collapse
            </Button>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default ListFiltersWidget;
